import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../../api/adminApi';
import { SUBJECT_NAMES } from '@padcanvas/shared';

interface EligibilityTabProps {
  userId: string;
}

export function EligibilityTab({ userId }: EligibilityTabProps) {
  const { data: userEligibility, isLoading: eligibilityLoading } = useQuery({
    queryKey: ['userEligibility', userId],
    queryFn: () => adminApi.getUserEligibility(userId),
    enabled: !!userId,
  });

  return (
    <div className="features-section">
      {eligibilityLoading ? (
        <div className="loading">Loading eligibility...</div>
      ) : !userEligibility || userEligibility.subjectEligibilities.length === 0 ? (
        <p className="no-features-message">No eligibility rules defined. Add them in Settings &rarr; Eligibility tab.</p>
      ) : (
        <>
          {userEligibility.subjectEligibilities.map(({ subject, eligibilities }) => (
            <div key={subject} style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: 15, margin: '0 0 8px 0', textTransform: 'capitalize', color: 'var(--text-primary)' }}>
                {SUBJECT_NAMES[subject] || subject}
              </h3>
              <table className="dt dt--compact" style={{ fontSize: 13, marginBottom: 8 }}>
                <thead>
                  <tr>
                    <th style={{ padding: '6px 10px' }}>Eligibility</th>
                    <th style={{ padding: '6px 10px' }}>Status</th>
                    <th style={{ padding: '6px 10px' }}>Used / Limit</th>
                    <th style={{ padding: '6px 10px' }}>Valid Until</th>
                  </tr>
                </thead>
                <tbody>
                  {eligibilities.map((el) => {
                    const usage = el.usage;
                    const isBoolean = el.currentValue === 'enabled' || el.currentValue === 'disabled';
                    const statusStr = isBoolean
                      ? el.currentValue
                      : usage
                        ? usage.threshold === null ? 'unlimited' : usage.threshold === 0 ? 'disabled' : 'active'
                        : el.currentValue;
                    const periodLabel = el.resetPeriod === 'day' ? ' per day' : el.resetPeriod === 'week' ? ' per week' : el.resetPeriod === 'month' ? ' per month' : '';
                    const usageStr = isBoolean
                      ? '-'
                      : usage
                        ? usage.threshold === null
                          ? `${usage.used} / \u221E${periodLabel}`
                          : `${usage.used} / ${usage.threshold}${periodLabel}`
                        : '-';
                    return (
                      <tr key={el.name}>
                        <td style={{ padding: '6px 10px' }}>
                          <strong>{el.displayName}</strong>
                          <br />
                          <span style={{ fontSize: 11, color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>{el.name}</span>
                        </td>
                        <td style={{ padding: '6px 10px' }}>{statusStr}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{usageStr}</td>
                        <td style={{ padding: '6px 10px', fontSize: 12, color: 'var(--text-tertiary)' }}>
                          {usage?.validUntil ? new Date(usage.validUntil).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
